import { FormControl, FormGroup, FormArray } from "@angular/forms";
import { PropertyMetadata } from '../properties/property-types';
import { Type } from "@angular/compiler";
import { v4 as uuidv4 } from 'uuid';
import { PolygonElement } from "./polygon-element";

export abstract class LevelDesignerElementBase {
    abstract layerIndex : number;
    abstract TypeName: string;
    abstract ParentType : string | null;
    abstract ParentFormGroupName : string | null;
    public selected: boolean = false;
    protected DisplayAngle: number = 0; // in radians
    protected locked: boolean = false;
    public formGroup: FormGroup;

    // Global colors for selection and edit markers
    static selectionColor: string = '#0000FF';  // Blue
    static editModeColor: string = '#FF0000';   // Red
    static rotateHandleColor: string = '#00FF00'; // Green for rotate handle

    // Add new property to track current parent
    public currentParentElement: LevelDesignerElementBase | null = null;

    constructor(formGroup: FormGroup) {
        this.formGroup = formGroup;

        if (!formGroup.contains('Id')) {
            formGroup.addControl('Id', new FormControl(uuidv4()));
        }
    }

    abstract draw(ctx: CanvasRenderingContext2D): void;

    abstract drawGeo(ctx: CanvasRenderingContext2D): void;

    abstract isPointInside(x: number, y: number): boolean;

    abstract getBoundingBox(): { x: number, y: number, width: number, height: number };

    abstract move(deltaX: number, deltaY: number): void;

    select(): void {
        this.selected = true;
    }

    deselect(): void {
        this.selected = false;
    }

    isSelected(): boolean {
        return this.selected;
    }

    abstract clone(): LevelDesignerElementBase;

    abstract toJSON(): object;

    abstract fromJSON(json: object): void;

    getRotation(): number {
        return this.DisplayAngle;
    }

    setRotation(angle: number): void {
        this.DisplayAngle = angle;
    }

    rotate(angle: number, centerX: number, centerY: number): void {
        if (!this.locked) {
            this.DisplayAngle += angle;
            this.rotateImplementation(angle, centerX, centerY);
        }
    }

    protected abstract rotateImplementation(angle: number, centerX: number, centerY: number): void;

    getRotateHandlePosition(): { x: number, y: number } {
        const bbox = this.getBoundingBox();
        const centerX = bbox.x + bbox.width / 2;
        const centerY = bbox.y + bbox.height / 2;
        
        // Fixed distance from the bounding box
        const handleDistance = 40;

        // Calculate the position of the handle
        const handleX = centerX + Math.sin(this.DisplayAngle) * (bbox.height / 2 + handleDistance);
        const handleY = centerY - Math.cos(this.DisplayAngle) * (bbox.height / 2 + handleDistance);

        return { x: handleX, y: handleY };
    }

    scale(scaleX: number, scaleY: number, anchorX: number, anchorY: number): void {
        if (!this.locked) {
            this.scaleImplementation(scaleX, scaleY, anchorX, anchorY);
        }
    }

    protected abstract scaleImplementation(scaleX: number, scaleY: number, anchorX: number, anchorY: number): void;

    isLocked(): boolean {
        return this.locked;
    }

    toggleLock(): void {
        this.locked = !this.locked;
    }
    setLocked(locked: boolean): void {
        this.locked = locked;
    }

    public getPolygonPoints(): { x: number, y: number }[] {
        return [];
    }

    abstract getLineSegments(): { x1: number, y1: number, x2: number, y2: number }[];

    abstract default(): LevelDesignerElementBase;

    public checkPositionValid(elements: LevelDesignerElementBase[]): ElementValidationResult {
        const result: ElementValidationResult = {
            errors: [],
            parentElement: null,
            element: this
        };

        if (this.ParentType && this.ParentFormGroupName) {
            // Find current parent if not already set
            if (!this.currentParentElement) {
                // Search for this element's formGroup in all potential parents
                const potentialParents = elements.filter(e => e.TypeName === this.ParentType);
                for (const parent of potentialParents) {
                    const parentFormArray = parent.formGroup.get(this.ParentFormGroupName) as FormArray;
                    if (parentFormArray?.controls.includes(this.formGroup)) {
                        this.currentParentElement = parent;
                        break;
                    }
                }
            }

            // Find new potential parent based on position
            let newParentElement = null;
            for (const element of elements) {
                if (element.TypeName === this.ParentType && this.isInsidePolygon(element)) {
                    newParentElement = element;
                    break;
                }
            }

            result.parentElement = newParentElement;
            
            if (!newParentElement) {
                result.errors.push(`${this.TypeName} must be completely inside a ${this.ParentType}`);
            } else if (newParentElement !== this.currentParentElement) {
                this.handleParentChange(newParentElement);
            }
        }

        return result;
    }

    public checkPropertiesValid(allElements : LevelDesignerElementBase[]): ElementValidationResult {
        const result: ElementValidationResult = {
            errors: [],
            parentElement: null,
            element: this
        };

        // Get all property metadata for this element
        const properties = this.getPropertyMetadata();

        // Check each property's validation rules
        for (const property of properties) {
            if(property.formControlName) {
                const control = this.formGroup.get(property.formControlName);
                
                if (control) {
                    // Check required fields have values
                    if (property.required && !control.value) {
                        result.errors.push(`${property.displayName || property.formControlName} is required`);
                    }
                }
            }
        }

        return result;
    }

    protected handleParentChange(newParent: LevelDesignerElementBase): void {
        if (this.ParentFormGroupName) {
            // Remove from old parent if exists
            if (this.currentParentElement) {
                const oldFormArray = this.currentParentElement.formGroup.get(this.ParentFormGroupName) as FormArray;
                const index = oldFormArray?.controls.indexOf(this.formGroup);
                if (index >= 0) {
                    oldFormArray.removeAt(index);
                }
                if(this.currentParentElement instanceof PolygonElement) {
                    this.currentParentElement.elements.splice(this.currentParentElement.elements.indexOf(this), 1);
                }
            }

            // Add to new parent
            const newFormArray = newParent.formGroup.get(this.ParentFormGroupName) as FormArray;
            if (!newFormArray.controls.includes(this.formGroup)) {
                newFormArray.push(this.formGroup);
                if(newParent instanceof PolygonElement) {
                    newParent.elements.push(this);
                }
            }

            this.currentParentElement = newParent;
        }
    }

    // Abstract method for checking if element is inside a polygon
    protected abstract isInsidePolygon(polygonElement: LevelDesignerElementBase): boolean;

    abstract updateForm(): void;

    public getPropertyMetadata(): PropertyMetadata[] {
        return [];  // Base class returns empty array, subclasses will override
    }

    public getWidth(): number {
        const bbox = this.getBoundingBox();
        return bbox.width;
    }

    public setWidth(width: number): void {
        const bbox = this.getBoundingBox();
        const scale = width / bbox.width;
        this.scale(scale, 1, bbox.x, bbox.y + bbox.height/2);
        this.updateForm();
    }

    public getHeight(): number {
        const bbox = this.getBoundingBox();
        return bbox.height;
    }

    public setHeight(height: number): void {
        const bbox = this.getBoundingBox();
        const scale = height / bbox.height;
        this.scale(1, scale, bbox.x + bbox.width/2, bbox.y);
        this.updateForm();
    }
    public handleMouseDown(x: number, y: number): boolean {
        return false;
    }
    public handleMouseMove(x: number, y: number): boolean {
        return false;
    }
    public handleMouseUp(x: number, y: number): boolean {
        return false;
    }
    
    // Add public getter for DisplayAngle
    public getDisplayAngle(): number {
        return this.DisplayAngle;
    }

    public handleDoubleClick?(x: number, y: number): boolean;
}

export interface ElementValidationResult{
    errors: string[];
    parentElement: LevelDesignerElementBase | null;
    element: LevelDesignerElementBase;

}
